import keyBy from 'lodash/keyBy';
import { RefObject, useState, useMemo, useEffect, KeyboardEvent } from 'react';
import { ENTER_KEY, DOWN_ARROW } from 'constants/keyCodes';
import { DEFAULT_CATEGORY_ID } from '../constants';
import { BaseOption, SelectValueType } from '../types';
import { focusHandlerForOptionsList } from '../utils';

type UseTypeaheadTriggerArgs<TValue extends SelectValueType = string> = {
  isOpen: boolean;
  options: BaseOption<TValue>[];
  selectedOptions: BaseOption<TValue>[];
  optionsEl: RefObject<HTMLElement>;
  setFilterText: (value: any) => void;
  toggleDropdown: (isOpen?: boolean) => void;
  selectOption: (category: string, value: any) => void;
  onSearchChange?: (value: any) => void;
};

export const useTypeaheadTrigger = <TValue extends SelectValueType = string>({
  isOpen,
  options,
  optionsEl,
  selectedOptions,
  selectOption,
  setFilterText,
  toggleDropdown,
  onSearchChange,
}: UseTypeaheadTriggerArgs<TValue>) => {
  const [isDisplayingCustomInput, setIsDisplayingCustomInput] = useState(false);
  const { value, label } = selectedOptions?.[0] ?? {};
  const displayLabel = label?.toString() || value?.toString() || '';
  const [searchValue, setSearchValue] = useState<string>(value?.toString() || '');
  const keyedOptionsByLabel = useMemo(() => keyBy(options, 'label'), [options]);

  useEffect(() => {
    // we need this in case the value changes outside of this component.
    setSearchValue(displayLabel);
    setIsDisplayingCustomInput(false);
  }, [value]);

  const getSelectedValue = () => {
    // if the searchValue matches a label from options,
    // lets select that value from the corresponding option
    const optionFromLabel = keyedOptionsByLabel[searchValue];
    return optionFromLabel?.value || searchValue;
  };

  function onSearch(text: string) {
    setIsDisplayingCustomInput(true);
    setSearchValue(text);
    setFilterText(text);
    if (onSearchChange) {
      onSearchChange(text);
    }
    if (!isOpen) {
      toggleDropdown();
    }
  }

  const selectCurrentValue = (open: boolean) => {
    if (!open) {
      const selectedValue = getSelectedValue();
      selectOption(DEFAULT_CATEGORY_ID, selectedValue);
    }
  };

  const onKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    const { focusFirstItem } = focusHandlerForOptionsList(optionsEl);

    // keyCode should be refactored to this: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent
    const { keyCode, target } = e;

    switch (keyCode) {
      case ENTER_KEY: {
        e.preventDefault();
        // this sends Campaign despite needing to send campaignId
        const selectedValue = getSelectedValue();
        selectOption(DEFAULT_CATEGORY_ID, selectedValue);
        toggleDropdown(false);

        (target as HTMLInputElement).blur();
        break;
      }

      case DOWN_ARROW: {
        toggleDropdown(true);
        // after this, the input will no longer have focus
        focusFirstItem();
        break;
      }
      default:
        break;
    }
  };

  return {
    onSearch,
    onKeyPress,
    selectCurrentValue,
    value: isDisplayingCustomInput ? searchValue : displayLabel,
  };
};
